import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './FormsHistory.css';

function FormsHistory({ onClose, onLoadForm }) {
  const { user } = useAuth();
  
  // 🎯 États
  const [formulaires, setFormulaires] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // 🔍 Filtres
  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [equipementFilter, setEquipementFilter] = useState('');
  const [pagination, setPagination] = useState({ limit: 20, offset: 0, total: 0 });

  // 📥 Récupération des formulaires
  const fetchFormulaires = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
        search,
        statut: statutFilter,
        equipement: equipementFilter
      };
      
      // Récupérer le token d'authentification
      const token = localStorage.getItem('6w2h_token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }
      
      // Utiliser l'URL complète du backend pour éviter les problèmes de proxy
      const response = await axios.get('http://localhost:5000/api/formulaires/mes-formulaires', { 
        params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true  // Inclure les cookies d'authentification
      });
      
      setFormulaires(response.data.formulaires);
      setStats(response.data.stats);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
      
    } catch (err) {
      console.error('Erreur récupération historique:', err);
      setError('Erreur lors du chargement de l\'historique');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.limit, pagination.offset, search, statutFilter, equipementFilter]);

  // ⚡ Effet initial
  useEffect(() => {
    fetchFormulaires();
  }, [fetchFormulaires]);

  // 🔄 Reset pagination sur changement de filtres
  useEffect(() => {
    setPagination(prev => ({ ...prev, offset: 0 }));
  }, [search, statutFilter, equipementFilter]);

  // 🎨 Badge de statut
  const getStatusBadge = (statut) => {
    const badges = {
      'brouillon': { color: '#6b7280', icon: '📝', label: 'Brouillon' },
      'en_cours': { color: '#3b82f6', icon: '⚡', label: 'En cours' },
      'valide': { color: '#10b981', icon: '✅', label: 'Validé' },
      'rejete': { color: '#ef4444', icon: '❌', label: 'Rejeté' },
      'archive': { color: '#9ca3af', icon: '📦', label: 'Archivé' }
    };
    
    const badge = badges[statut] || badges.brouillon;
    
    return (
      <span 
        className="status-badge"
        style={{ backgroundColor: badge.color }}
      >
        {badge.icon} {badge.label}
      </span>
    );
  };

  // 🔧 Fonction de décodage HTML avancée (gère le double échappement)
  const decodeHtmlEntities = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    let decoded = text;
    let previousDecoded = '';
    
    // Décodage récursif jusqu'à ce qu'il n'y ait plus de changement
    while (decoded !== previousDecoded) {
      previousDecoded = decoded;
      decoded = decoded
        // Entités multiples (double échappement)
        .replace(/&amp;amp;/g, '&')
        .replace(/&amp;quot;/g, '"')
        .replace(/&amp;#x27;/g, "'")
        .replace(/&amp;#x2F;/g, '/')
        .replace(/&amp;#x3A;/g, ':')
        .replace(/&amp;lt;/g, '<')
        .replace(/&amp;gt;/g, '>')
        // Entités simples
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#x27;/g, "'")
        .replace(/&#x2F;/g, '/')
        .replace(/&#x3A;/g, ':')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        // Entités pour caractères accentués
        .replace(/&eacute;/g, 'é')
        .replace(/&egrave;/g, 'è')
        .replace(/&ecirc;/g, 'ê')
        .replace(/&agrave;/g, 'à')
        .replace(/&acirc;/g, 'â')
        .replace(/&ccedil;/g, 'ç')
        .replace(/&ocirc;/g, 'ô')
        .replace(/&ucirc;/g, 'û')
        .replace(/&ugrave;/g, 'ù')
        .replace(/&icirc;/g, 'î')
        .replace(/&iuml;/g, 'ï');
    }
    
    return decoded;
  };

  // 📅 Format de date
  const formatDate = (dateString) => {
    const decodedDate = decodeHtmlEntities(dateString);
    return new Date(decodedDate).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 💰 Format de coût
  const formatCost = (cost) => {
    if (!cost || cost === 0) return '-';
    return `${cost.toLocaleString('fr-FR')} €`;
  };

  // 📄 Pagination
  const goToPage = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="forms-history">
      {/* 📋 En-tête */}
      <div className="history-header">
        <div className="header-info">
          <h2>📋 Mon Historique 6W2H</h2>
          <p>Tous mes formulaires créés • {user?.prenom} {user?.nom}</p>
        </div>
        <button className="close-button" onClick={onClose}>✕</button>
      </div>

      {/* 📊 Statistiques personnelles */}
      <div className="personal-stats">
        <div className="stat-card">
          <div className="stat-number">{stats.total || 0}</div>
          <div className="stat-label">📋 Total</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.en_cours || 0}</div>
          <div className="stat-label">⚡ En cours</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.valides || 0}</div>
          <div className="stat-label">✅ Validés</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{formatCost(stats.cout_moyen)}</div>
          <div className="stat-label">💰 Coût moyen</div>
        </div>
      </div>

      {/* 🔍 Filtres */}
      <div className="filters-section">
        <div className="filter-group">
          <input
            type="text"
            placeholder="🔍 Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <select
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">Tous les statuts</option>
            <option value="brouillon">📝 Brouillon</option>
            <option value="en_cours">⚡ En cours</option>
            <option value="valide">✅ Validé</option>
            <option value="rejete">❌ Rejeté</option>
            <option value="archive">📦 Archivé</option>
          </select>
        </div>
        
        <div className="filter-group">
          <input
            type="text"
            placeholder="Équipement..."
            value={equipementFilter}
            onChange={(e) => setEquipementFilter(e.target.value)}
            className="filter-input"
          />
        </div>
        
        <button 
          className="refresh-button"
          onClick={fetchFormulaires}
          disabled={isLoading}
        >
          🔄 Actualiser
        </button>
      </div>

      {/* ⚠️ Erreur */}
      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {/* 📋 Liste des formulaires */}
      <div className="forms-list">
        {isLoading ? (
          <div className="loading-state">
            <div className="loading-spinner">⏳</div>
            <p>Chargement de votre historique...</p>
          </div>
        ) : formulaires.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>Aucun formulaire trouvé</h3>
            <p>Vous n'avez pas encore créé de formulaires ou aucun ne correspond à vos critères.</p>
          </div>
        ) : (
          formulaires.map(form => (
            <div key={form.id} className="form-item">
              <div className="form-header">
                <div className="form-title">
                  <strong>{decodeHtmlEntities(form.equipement_zone_ligne)}</strong>
                  {getStatusBadge(form.statut)}
                </div>
                <div className="form-actions">
                  {form.statut !== 'rejete' && form.statut !== 'archive' ? (
                    <button
                      className="load-button"
                      onClick={() => onLoadForm(form.id)}
                      title="Charger ce formulaire pour modification"
                    >
                      📂 Charger
                    </button>
                  ) : (
                    <span 
                      className="load-disabled"
                      title={`Formulaire ${form.statut === 'rejete' ? 'rejeté' : 'archivé'} - Chargement interdit`}
                    >
                      🔒 {form.statut === 'rejete' ? 'Rejeté' : 'Archivé'}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="form-content">
                <div className="form-description">
                  <strong>Problème :</strong> {decodeHtmlEntities(form.probleme_initial || '').substring(0, 100)}
                  {(form.probleme_initial || '').length > 100 ? '...' : ''}
                </div>
                
                <div className="form-meta">
                  <span className="form-date">
                    📅 {formatDate(form.updated_at)}
                  </span>
                  {form.cout_estime > 0 && (
                    <span className="form-cost">
                      💰 {formatCost(form.cout_estime)}
                    </span>
                  )}
                  {form.priorite && form.priorite !== 'normale' && (
                    <span className={`priority-badge priority-${form.priorite}`}>
                      🔥 {form.priorite}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 📄 Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-button"
            onClick={() => goToPage(0)}
            disabled={currentPage === 1}
          >
            ⏮️ Premier
          </button>
          
          <button
            className="page-button"
            onClick={() => goToPage(pagination.offset - pagination.limit)}
            disabled={currentPage === 1}
          >
            ⬅️ Précédent
          </button>
          
          <span className="page-info">
            Page {currentPage} sur {totalPages} 
            ({pagination.total} formulaires)
          </span>
          
          <button
            className="page-button"
            onClick={() => goToPage(pagination.offset + pagination.limit)}
            disabled={currentPage === totalPages}
          >
            Suivant ➡️
          </button>
          
          <button
            className="page-button"
            onClick={() => goToPage((totalPages - 1) * pagination.limit)}
            disabled={currentPage === totalPages}
          >
            Dernier ⏭️
          </button>
        </div>
      )}
    </div>
  );
}

export default FormsHistory;
